import { memo } from "react";
import { CalendarCheck, Gift } from "lucide-react";
import type { BotezBokehTemplateProps } from "./types";
import {
  CORAL,
  CORAL_TEXT,
  CORAL_TEXT_LARGE,
  MUTED_TEXT,
  FONT_KICKER,
  FONT_SCRIPT,
  TEAL,
  TEAL_DARK,
  formatDate,
  getEventKind,
  isEventEveOrDay,
  isRsvpCutoffPassed,
} from "./utils";
import { getCountryDisplayName } from "@/utils/eventUtils";

export const BotezBokehHero = memo(({ event, config, locale, t, onRsvpClick }: BotezBokehTemplateProps) => {
  const localeCode = (locale || "ro") as "ro" | "en";
  const heroTagline = event.heroText?.[localeCode] || event.heroText?.ro || event.heroText?.en;
  const kind = getEventKind(event);

  // Big script title: baptism-first, wedding fallback (rule 2)
  const heroTitle = kind.isBaptismOnly
    ? kind.babyNames
      ? t("events.form.heroTitleBaptismOnly", { name: kind.babyNames })
      : event.name
    : kind.isWeddingAndBaptism
      ? t("events.form.heroTitleWeddingAndBaptism")
      : kind.coupleNames || event.name;

  const dateLabelKey = kind.isBaptismOnly
    ? "events.form.dateLabelBaptism"
    : kind.isWeddingAndBaptism
      ? "events.form.dateLabelWeddingAndBaptism"
      : "events.form.dateLabelWedding";

  const countryName = getCountryDisplayName(event.country, locale);
  const heroImage = event.cover;
  const hasVisibleGiftRegistry = Boolean(
    event.registryPublic && event.giftsActive && (event as unknown as Record<string, boolean>).hasEnabledGifts
  );
  const promoteGiftCta =
    hasVisibleGiftRegistry && (isRsvpCutoffPassed(event) || isEventEveOrDay(event));
  const rsvpVisible =
    Boolean(event.rsvpSettings?.rsvpEnabled) && !isRsvpCutoffPassed(event) && !promoteGiftCta;
  const showGiftFallback =
    Boolean(event.rsvpSettings?.rsvpEnabled) && promoteGiftCta;

  return (
    <section
      id="home"
      className="relative min-h-[100dvh] flex items-center justify-center overflow-hidden"
      style={{ background: config.hero.background }}
    >
      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-28 pb-16 text-center relative z-20 w-full">
        {/* Names with decorative blob shape behind (CSS/SVG, no raster assets) */}
        <div className="mb-8 relative">
          <div className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
            <svg
              viewBox="0 0 500 300"
              className="w-full max-w-3xl h-auto opacity-10"
              style={{ filter: "blur(2px)" }}
            >
              <path
                d="M 250,20 Q 450,50 470,150 Q 450,250 250,280 Q 50,250 30,150 Q 50,50 250,20 Z"
                fill={CORAL}
              />
            </svg>
          </div>

          <div className="relative py-8 px-2 sm:px-6">
            {heroTagline && (
              <p
                className="text-2xl sm:text-3xl md:text-4xl mb-8 break-words"
                style={{ fontFamily: FONT_SCRIPT, color: TEAL }}
              >
                {heroTagline}
              </p>
            )}
            <h1
              className="text-5xl sm:text-6xl md:text-7xl leading-relaxed break-words [overflow-wrap:normal] [word-break:keep-all] hyphens-none"
              style={{ fontFamily: FONT_SCRIPT, color: CORAL_TEXT_LARGE }}
            >
              {heroTitle.replace(/-/g, "\u2011")}
            </h1>
            {/* Wedding + baptism: show all celebrated names under the combined title */}
            {kind.isWeddingAndBaptism && kind.coupleNames && (
              <p
                className="text-2xl sm:text-3xl mt-6 break-words"
                style={{ fontFamily: FONT_SCRIPT, color: TEAL }}
              >
                {kind.coupleNames}
              </p>
            )}
            {kind.isWeddingAndBaptism && kind.babyNames && (
              <p
                className="text-xl sm:text-2xl mt-2 break-words"
                style={{ fontFamily: FONT_SCRIPT, color: CORAL_TEXT }}
              >
                {kind.babyNames}
              </p>
            )}
          </div>
        </div>

        {/* Date & location */}
        {event.date && (
          <p
            className="-mt-4 mb-6 text-xs sm:text-sm tracking-[0.15em] uppercase break-words"
            style={{ fontFamily: FONT_KICKER, color: TEAL_DARK }}
          >
            {t(dateLabelKey)}{" "}
            <span className="whitespace-nowrap">
              {formatDate(event.date, locale, { day: "numeric", month: "long", year: "numeric" })}
            </span>
            {countryName && <span className="whitespace-nowrap">{` · ${countryName}`}</span>}
          </p>
        )}

        {/* Photo in a double-ring coral/teal frame */}
        {heroImage && (
          <div className="mb-8 flex justify-center">
            <div
              className="rounded-[2.5rem] p-[3px] shadow-2xl"
              style={{ background: `linear-gradient(135deg, ${CORAL}, ${TEAL})` }}
            >
              <div className="rounded-[2.2rem] bg-white p-[4px]">
                <div className="w-64 h-80 sm:w-72 sm:h-96 rounded-[2rem] overflow-hidden">
                  <img
                    src={heroImage}
                    alt={kind.babyNames || kind.coupleNames || event.name}
                    className="w-full h-full object-cover"
                    loading="eager"
                    decoding="async"
                    fetchPriority="high"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* RSVP CTA */}
        {rsvpVisible && (
          <div className="flex flex-col items-center gap-3">
            <button
              type="button"
              onClick={() => {
                if (onRsvpClick) {
                  onRsvpClick();
                } else {
                  document.getElementById("rsvp")?.scrollIntoView({ behavior: "smooth", block: "start" });
                }
              }}
              className="inline-flex items-center gap-3 px-10 py-5 text-white text-sm uppercase tracking-[0.15em] transition-all duration-300 hover:scale-105 shadow-lg font-medium"
              style={{
                fontFamily: FONT_KICKER,
                backgroundColor: TEAL,
                clipPath: "polygon(8% 0%, 92% 0%, 100% 8%, 100% 92%, 92% 100%, 8% 100%, 0% 92%, 0% 8%)",
                borderRadius: "4px",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = TEAL_DARK)}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = TEAL)}
            >
              <CalendarCheck className="h-5 w-5" aria-hidden="true" />
              {t("events.form.template.rsvpNow")}
            </button>
            {event.rsvpCutoffDate && (
              <p
                className="text-[11px] uppercase tracking-[0.15em]"
                style={{ fontFamily: FONT_KICKER, color: MUTED_TEXT }}
              >
                {t("events.form.rsvpDeadline")}:{" "}
                {formatDate(event.rsvpCutoffDate, locale, { day: "numeric", month: "long", year: "numeric" })}
              </p>
            )}
          </div>
        )}

        {/* JUB-23: after the RSVP cutoff, point guests to the gift registry instead */}
        {showGiftFallback && (
          <button
            type="button"
            onClick={() => {
              document.getElementById("gifts")?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            className="inline-flex items-center gap-3 px-10 py-5 text-white text-sm uppercase tracking-[0.15em] transition-all duration-300 hover:scale-105 shadow-lg font-medium rounded-full"
            style={{ fontFamily: FONT_KICKER, backgroundColor: CORAL_TEXT }}
          >
            <Gift className="h-5 w-5" aria-hidden="true" />
            {t("events.form.template.contributeGift")}
          </button>
        )}
      </div>
    </section>
  );
});

BotezBokehHero.displayName = "BotezBokehHero";
